import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePriceIndexStore } from '../../store/usePriceIndexStore.ts';
import { Card } from '../../components/Shared/Card.tsx';
import { Loading } from '../../components/Shared/Loading.tsx';
import { Button } from '../../components/Shared/Button.tsx';
import { EmptyState } from '../../components/Shared/EmptyState.tsx';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../components/Shared/Table.tsx';
import { ArrowLeft, CheckCircle, Download, AlertTriangle } from 'lucide-react';
import { formatMoney } from '../../utils/money';
import { formatPercent } from '../../utils/percent';

export default function PriceIndexPreview() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['priceIndex', 'common']);
  const navigate = useNavigate();
  const {
    selectedPriceIndex,
    previewData,
    loading,
    saving,
    error,
    getPriceIndexById,
    previewPriceIndex,
    applyPriceIndex,
  } = usePriceIndexStore();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (!id) return;

    Promise.all([
      getPriceIndexById(id),
      previewPriceIndex(id),
    ]).then(() => setInitialLoadComplete(true));
  }, [id, getPriceIndexById, previewPriceIndex]);

  const handleApply = async () => {
    if (!id) return;

    try {
      await applyPriceIndex(id);
      setShowConfirmDialog(false);
      navigate('/price-indexes/history');
    } catch (error) {
      console.error(t('priceIndex:errors.applyError'), error);
    }
  };

  const handleExportCSV = () => {
    if (!previewData?.changes) return;

    const headers = ['Service', 'Service-Nr.', 'Alter Preis', 'Neuer Preis', 'Änderung', '%'];
    const rows = previewData.changes.map(change => [
      change.serviceName || '',
      change.serviceNumber || '',
      change.oldPrice?.toFixed(2) || '0.00',
      change.newPrice?.toFixed(2) || '0.00',
      change.priceIncrease?.toFixed(2) || '0.00',
      change.percentageChange?.toFixed(2) || '0.00',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `preisindex-vorschau-${selectedPriceIndex?.designation || 'export'}.csv`;
    link.click();
  };

  const calculateAverage = (values: number[]) => {
    if (values.length === 0) return 0;
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  const getPriceChangeColor = (change?: number) => {
    if (!change || change === 0) return 'text-gray-600 dark:text-gray-400';
    return change > 0 
      ? 'text-green-600 dark:text-green-400' 
      : 'text-red-600 dark:text-red-400';
  };

  if (loading && !initialLoadComplete) return <Loading />;

  if (error && !initialLoadComplete) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/price-indexes/history')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-3xl font-bold text-text-primary dark:text-slate-50">
            {t('priceIndex:preview.title')} 
          </h1>
        </div>
        <Card>
          <EmptyState
            variant="error"
            title={t('priceIndex:errors.previewLoadError')}
            description={error || t('priceIndex:errors.previewLoadErrorDescription')}
            onRetry={() => id && previewPriceIndex(id)}
          />
        </Card>
      </div>
    );
  }

  if (!selectedPriceIndex || !previewData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/price-indexes/history')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-3xl font-bold text-text-primary dark:text-slate-50">
            {t('priceIndex:preview.notFound')}
          </h1>
        </div>
      </div>
    );
  }

  const avgChange = previewData.changes
    ? calculateAverage(previewData.changes.map(c => c.percentageChange || 0))
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/price-indexes/history')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-text-primary dark:text-slate-50">
              {t('priceIndex:preview.title')} {selectedPriceIndex.designation}
            </h1>
            <p className="text-text-secondary dark:text-slate-400 mt-1">
              {previewData.wasApplied ? t('priceIndex:preview.alreadyApplied') : t('priceIndex:preview.notApplied')}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          {previewData.changes && previewData.changes.length > 0 && (
            <Button variant="outline" onClick={handleExportCSV} leftIcon={<Download className="w-4 h-4" />}>
              {t('priceIndex:buttons.exportCSV')}
            </Button>
          )}
          {!previewData.wasApplied && (
            <Button
              variant="primary"
              onClick={() => setShowConfirmDialog(true)}
              disabled={saving}
              leftIcon={<CheckCircle className="w-4 h-4" />}
            >
              {saving ? t('priceIndex:buttons.applying') : t('priceIndex:buttons.apply')}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('priceIndex:summary.affectedServices')}</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {previewData.affectedServicesCount || 0}
            </p>
          </div>
        </Card>
        <Card>
          <div className="p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('priceIndex:summary.factor')}</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {selectedPriceIndex.factor?.toFixed(4)}
            </p>
            <p className={`text-sm mt-1 ${getPriceChangeColor(((selectedPriceIndex.factor || 1) - 1) * 100)}`}>
              {formatPercent(((selectedPriceIndex.factor || 1) - 1) * 100, { signed: true, fractionDigits: 2 })}
            </p>
          </div>
        </Card>
        <Card>
          <div className="p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('priceIndex:summary.averageChange')}</p>
            <p className={`text-3xl font-bold ${getPriceChangeColor(avgChange)}`}>
              {formatPercent(avgChange, { signed: true, fractionDigits: 2 })}
            </p>
          </div>
        </Card>
      </div>

      {/* Status Badge */}
      {previewData.wasApplied && (
        <Card>
          <div className="p-4 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-500">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{t('priceIndex:preview.alreadyApplied')}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('priceIndex:preview.appliedOn')} {previewData.appliedAt ? new Date(previewData.appliedAt).toLocaleString('de-DE') : '-'}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Changes Table */}
      {previewData.changes && previewData.changes.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>{t('priceIndex:changes.service')}</TableHeader>
                  <TableHeader>{t('priceIndex:changes.serviceNumber')}</TableHeader>
                  <TableHeader className="text-right">{t('priceIndex:changes.oldPrice')}</TableHeader>
                  <TableHeader className="text-right">{t('priceIndex:changes.newPrice')}</TableHeader>
                  <TableHeader className="text-right">{t('priceIndex:changes.change')}</TableHeader>
                  <TableHeader className="text-right">{t('priceIndex:changes.percentage')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.changes.map((change, index) => {
                  const priceChange = change.priceIncrease ?? 0;
                  const getColor = () => {
                    if (priceChange > 0) return '#16a34a'; // green-600
                    if (priceChange < 0) return '#dc2626'; // red-600
                    return '#4b5563'; // gray-600
                  };
                  const color = getColor();
                  
                  return (
                    <TableRow key={change.serviceId || index}>
                      <TableCell>{change.serviceName || '-'}</TableCell>
                      <TableCell>{change.serviceNumber || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatMoney(change.oldPrice)}
                      </TableCell>
                      <TableCell className="text-right font-mono" style={{ color }}>
                        {formatMoney(change.newPrice)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold" style={{ color }}>
                        {formatMoney(change.priceIncrease, { signed: true })}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold" style={{ color }}>
                        {formatPercent(change.percentageChange, { signed: true, fractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <EmptyState
          title={t('priceIndex:empty.noChanges')}
          description={t('priceIndex:empty.noChangesDescription')}
        />
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowConfirmDialog(false)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Preisindex anwenden?
                </h3>
              </div>
              
              <div className="space-y-3 mb-6">
                <p className="text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: t('priceIndex:confirm.description', { designation: selectedPriceIndex.designation }) }} />
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{t('priceIndex:confirm.servicesWillUpdate', { count: previewData.affectedServicesCount })}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{t('priceIndex:confirm.changesLogged')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">{t('priceIndex:confirm.cannotUndo')}</span>
                  </li>
                </ul>
                <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                  {t('priceIndex:confirm.question')}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirmDialog(false)}
                  disabled={saving}
                  className="flex-1"
                >
                  {t('priceIndex:buttons.cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleApply}
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? t('priceIndex:buttons.applying') : t('priceIndex:buttons.applyNow')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
